import React, { useMemo, useState } from 'react';
import { ethers } from 'ethers';
import Button from '../common/Button';
import { useWallet } from '../../context/WalletContext';
import { useContractState } from '../../context/ContractContext';
import { useNotify } from '../../context/NotificationContext';
import { getADNFTContract, getUSDTContract, getADNFTAddress } from '../../web3/contractInstance';
import { validateInvestment } from '../../utils/validators';
import { parseContractError } from '../../utils/errors';
import { toNumberSafe, formatUSDT } from '../../utils/format';
import { toUnits } from '../../web3/providerHelper';
import FullScreenLoader from '../loader/FullScreenLoader';

const QUICK = [50, 100, 500, 1000];

export default function InvestForm() {
  const { getSigner } = useWallet();
  const { limits, usdtBalance, usdtAllowance, refresh } = useContractState();
  const notify = useNotify();
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [busyMsg, setBusyMsg] = useState('');

  const err = useMemo(() => amount ? validateInvestment(amount, limits) : null, [amount, limits]);
  const balNum = toNumberSafe(usdtBalance);

  const min = limits ? toNumberSafe(limits.min) : 50;
  const mult = limits ? toNumberSafe(limits.multiple) : 50;

  async function ensureApproval(signer, needed) {
    const usdt = getUSDTContract(signer);
    const cur = await usdt.allowance(await signer.getAddress(), getADNFTAddress());
    if (cur.gte(needed)) return;
    setBusyMsg('Approving USDT…');
    const tx = await usdt.approve(getADNFTAddress(), ethers.constants.MaxUint256);
    await tx.wait();
    notify.success('USDT approved');
  }

  async function submit() {
    if (err) { notify.error('Invalid amount', err); return; }
    const signer = getSigner();
    if (!signer) { notify.error('Wallet not ready'); return; }
    const value = toUnits(amount);
    if (Number(amount) > balNum) { notify.error('Insufficient USDT balance'); return; }

    setBusy(true);
    setBusyMsg('Preparing transaction…');
    try {
      await ensureApproval(signer, value);
      setBusyMsg('Confirm investment in your wallet…');
      const c = getADNFTContract(signer);
      const tx = await c.invest(value);
      setBusyMsg('Waiting for confirmation…');
      await tx.wait();
      notify.success('Investment successful', `$${amount} USDT staked`);
      setAmount('');
      refresh();
    } catch (e) {
      notify.error('Transaction failed', parseContractError(e));
    } finally {
      setBusy(false);
      setBusyMsg('');
    }
  }

  return (
    <div className="card invest-card">
      <div className="row-between mb-8">
        <h3>Invest USDT</h3>
        <span className="badge success">Bal: {formatUSDT(usdtBalance)}</span>
      </div>
      <div className="field">
        <label>Amount (USDT)</label>
        <div className="input-suffix">
          <input
            className="input"
            type="number"
            inputMode="decimal"
            placeholder={`min $${min}, step $${mult}`}
            value={amount}
            onChange={e => setAmount(e.target.value)}
          />
          <span className="suffix">USDT</span>
        </div>
        {err && <div className="error-msg">⚠ {err}</div>}
      </div>
      <div className="invest-quick">
        {QUICK.map(q => (
          <button key={q} className={`chip ${String(q) === amount ? 'active' : ''}`} onClick={() => setAmount(String(q))}>${q}</button>
        ))}
      </div>
      <Button neon block size="lg" onClick={submit} loading={busy} disabled={!amount || !!err}>
        {usdtAllowance && ethers.BigNumber.from(usdtAllowance).gte(toUnits(amount || '0')) ? 'Invest Now' : 'Approve & Invest'}
      </Button>
      <div className="hint-msg mt-8">
        80% funds the trading pool; 20% is retained for trading-profit + level income distributions.
      </div>
      {busy && <FullScreenLoader message={busyMsg || 'Please wait…'} sub="Do not close this window" />}
    </div>
  );
}
